import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../../data/content";
import { EASE } from "./Reveal";

const HeroLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.5, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onExplore }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Candlelit editorial tablescape by SORA EVENTS HOUSE"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.8, ease: EASE }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#2e2f1e]/65 via-[#2e2f1e]/40 to-[#2e2f1e]/75" />

      <motion.div
        style={{ y: contentY, opacity }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <h1 data-testid="hero-title" className="leading-none">
          <HeroLine delay={0.65}>
            <img
              src="/assets/logo-ivory.png"
              alt="SORA events house"
              className="w-[clamp(300px,46vw,620px)]"
            />
          </HeroLine>
        </h1>
        <HeroLine delay={1.1}>
          <span className="mt-8 block font-serif text-xl font-light italic text-ivory/90 sm:text-2xl">
            Celebrations, thoughtfully created.
          </span>
        </HeroLine>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.4 }}
        className="absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 text-[10px] font-medium uppercase tracking-[0.5em] text-ivory/70 md:block"
        style={{ writingMode: "vertical-rl" }}
      >
        Est. Dubai — MMXXVI
      </motion.p>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1.4 }}
        onClick={onExplore}
        data-testid="hero-explore-button"
        className="absolute bottom-9 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-ivory/80 transition-colors duration-500 hover:text-ivory"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.45em]">
          Explore Our World
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={15} strokeWidth={1.25} />
        </motion.span>
      </motion.button>
    </section>
  );
};
